import {
  DATA_STATUS,
  NOT_FOUND_ERROR_404,
  PROPERTY_DETAILS_TAB_NAMES,
} from '@/constants';
import { fetchPropertyDetailsData } from '@/data/data-actions';
import * as dataSelectors from '@/data/data-selectors';
import { smartEncodeURIComponent } from '@/utils/uri-utils';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { usePlaceSpecificUrl } from '../usePlaceSpecificUrl';
import { useOptionalPropertyDeckardId } from '../usePropertyDeckardId';
import { useRootTab } from '../useRootTab';

export const usePropertyDetails = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { getPlaceSpecificUrl } = usePlaceSpecificUrl();
  const { propertyDeckardId: optionalPropertyDeckardId } =
    useOptionalPropertyDeckardId();
  const currentPropertyState = useSelector(dataSelectors.propertyData);
  const { activeTab } = useRootTab();
  const propertyDetailsTabs = useSelector(dataSelectors.propertyDetailsTabs);

  /**
   * Generates a URL for the property details page.
   *
   * @param propertyDeckardId - The Deckard ID of the property. It can be either encoded or not.
   * @param propertyDetailsTab - The specific tab on the property details page. Defaults to overview tab.
   * @param tabToUse - The root tab. Defaults to activeTab from hook scope.
   * @returns The generated URL string.
   */
  const generatePropertyDetailsLink = useCallback(
    ({
      propertyDeckardId,
      propertyDetailsTab = PROPERTY_DETAILS_TAB_NAMES.overview,
      tab,
    }: {
      propertyDeckardId: string;
      propertyDetailsTab?: string;
      tab?: string; // Optional, will use activeTab if not provided
    }): string => {
      const encodedPropertyDeckardId =
        smartEncodeURIComponent(propertyDeckardId);
      const targetTab = tab || activeTab;
      return getPlaceSpecificUrl(
        `${targetTab}/${encodedPropertyDeckardId}/${propertyDetailsTab}`
      );
    },
    [getPlaceSpecificUrl, activeTab]
  );

  useEffect(() => {
    if (!optionalPropertyDeckardId) {
      return;
    }
    const encodedPropertyDeckardId = smartEncodeURIComponent(
      optionalPropertyDeckardId
    );

    dispatch(
      fetchPropertyDetailsData({
        propertyDeckardID: encodedPropertyDeckardId,
      })
    );
  }, [optionalPropertyDeckardId, dispatch]);

  /**
   * Navigates to the property details page for the given property ID and tab.
   *
   * @param propertyDeckardId - The Deckard ID of the property to navigate to. It can be either encoded or not.
   * @param propertyDetailsTab - The specific tab to open on the property details page. Defaults to overview.
   * @param tab - The source URL from which the navigation is initiated. Defaults to home.
   * @param replace - If true, replaces the current entry in the history stack instead of pushing a new one. Defaults to false.
   */
  const goToPropertyDetails = useCallback(
    ({
      propertyDeckardId,
      propertyDetailsTab = PROPERTY_DETAILS_TAB_NAMES.overview,
      tab,
      replace = false,
    }: {
      propertyDeckardId: string;
      propertyDetailsTab?: string;
      tab?: string;
      replace?: boolean;
    }) => {
      const link = generatePropertyDetailsLink({
        propertyDeckardId,
        propertyDetailsTab,
        tab,
      });
      history[replace ? 'replace' : 'push'](link);
    },
    [history, generatePropertyDetailsLink]
  );

  // There is temporary workaround to show loading state when currentProperty doesn't exist
  const loading =
    !currentPropertyState || currentPropertyState === DATA_STATUS.inProgress;

  const hasError = currentPropertyState === DATA_STATUS.dataError;

  const notFound = currentPropertyState?.Code === NOT_FOUND_ERROR_404;

  // This temporary workaround ensures that currentProperty always holds a valid property object rather than inflight string states
  // currentProperty is null when there is error or data is loading
  const currentProperty =
    !currentPropertyState || typeof currentPropertyState === 'string'
      ? null
      : currentPropertyState;

  return {
    goToPropertyDetails,
    generatePropertyDetailsLink,
    currentProperty,
    loading,
    notFound,
    hasError,
    propertyDetailsTabs,
  };
};
